import type { CommunityDua } from '../../types/communityDua'
import { FaHeart, FaRegHeart, FaEdit, FaTrash, FaPause, FaClock, FaAngleDoubleDown } from 'react-icons/fa'
import StopWatch from '@components/shared/Stopwatch/Stopwatch'
import SettingsModal from '@components/shared/SettingsModal/SettingsModal'
import Link from 'next/link'

interface CommunityDuaHeaderProps {
  dua: CommunityDua
  isAuthor: boolean
  userIsAdmin: boolean
  isLiked: boolean
  liking: boolean
  onLike: () => void
  onDelete: () => void
  deleting: boolean
  isScrolling: boolean
  isPending: boolean
  toggleScroll: () => void
  duration: number
  setDuration: (_d: number) => void
  statusLabels: Record<string, string>
}

export default function CommunityDuaHeader({
  dua,
  isAuthor,
  userIsAdmin,
  isLiked,
  liking,
  onLike,
  onDelete,
  deleting,
  isScrolling,
  isPending,
  toggleScroll,
  duration,
  setDuration,
  statusLabels,
}: CommunityDuaHeaderProps) {
  return (
    <div className="mb-6 space-y-4">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex-1 space-y-1.5">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-bold tracking-tight text-main sm:text-3xl">
              {dua.title}
            </h1>
            {isAuthor && (
              <span className="inline-flex items-center rounded-full bg-surface-strong px-2.5 py-0.5 text-[11px] font-semibold text-muted ring-1 ring-inset ring-border-main">
                {statusLabels[dua.status] ?? dua.status}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <div className="h-6 w-6 rounded-full bg-brand-secondary/10 flex items-center justify-center text-brand-secondary font-bold text-[10px]">
              {(dua.authorName || 'A')[0].toUpperCase()}
            </div>
            <p className="text-xs font-medium text-muted">
              Paylaşan: <span className="text-main">{dua.authorName || 'Anonim'}</span>
            </p>
            <div className="h-1 w-1 rounded-full bg-border-main" />
            <span className="text-xs text-muted font-medium">
              {dua.likeCount} beğeni
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2 self-end sm:self-start">
          <button
            type="button"
            onClick={onLike}
            disabled={liking}
            className={`flex items-center gap-2 rounded-xl border border-main px-3.5 py-2 text-sm font-semibold transition-all hover:bg-surface-subtle disabled:opacity-50 active:scale-95 ${
              isLiked 
                ? 'bg-brand-secondary/10 text-brand-secondary border-brand-secondary/30 shadow-sm' 
                : 'bg-bg-main text-main hover:text-brand-secondary'
            }`}
            title={isLiked ? 'Beğeniyi kaldır' : 'Beğen'}
          >
            {isLiked ? <FaHeart size={16} /> : <FaRegHeart size={16} />}
            <span>{isLiked ? 'Beğenildi' : 'Beğen'}</span>
          </button>

          {(isAuthor || userIsAdmin) && (
            <div className="flex gap-2">
              <Link
                href={`/sizden-gelen-dualar/duzenle?id=${dua.slug}`}
                className="inline-flex items-center justify-center rounded-xl border border-main bg-bg-main p-2 text-main hover:bg-surface-subtle transition-colors"
                title="Düzenle"
              >
                <FaEdit size={16} />
              </Link>
              <button
                type="button"
                onClick={onDelete}
                disabled={deleting}
                className="inline-flex items-center justify-center rounded-xl border border-danger/20 bg-danger/5 p-2 text-danger hover:bg-danger/10 transition-colors"
                title="Sil"
              >
                <FaTrash size={16} />
              </button>
            </div>
          )}
        </div>
      </div>

      {dua.description && (
        <div className="relative rounded-2xl bg-surface-subtle p-4 border border-main shadow-sm texture-noise">
          <p className="text-sm leading-6 text-main/80 italic">
            &ldquo;{dua.description}&rdquo;
          </p>
        </div>
      )}

      <div className="flex items-center justify-between border-y border-main/50 py-3 mt-4">
        <StopWatch
          className="text-sm font-semibold text-main/70 bg-surface-strong/50 px-3 py-1.5 rounded-lg"
          width={90}
          getTime={() => {}}
        />

        <div className="flex items-center gap-2.5 rounded-2xl border border-main bg-surface-subtle p-1.5 shadow-sm">
          <button
            onClick={toggleScroll}
            className={`flex h-9 items-center gap-2 rounded-xl px-4 py-1.5 text-xs font-semibold transition-all ${
              isScrolling 
                ? 'bg-brand-secondary text-white shadow-md' 
                : 'text-main hover:bg-surface-strong'
            }`}
            aria-label={isScrolling ? 'Durdur' : 'Kaydır'}
            title={isScrolling ? 'Durdur' : 'Otomatik Kaydır'}
          >
            {isScrolling ? (
              <>
                <FaPause size={14} className="animate-pulse" />
                <span>Durdur</span>
              </>
            ) : isPending ? (
              <>
                <FaClock size={14} className="animate-pulse text-brand-secondary" />
                <span className="text-brand-secondary">Bekliyor</span>
              </>
            ) : (
              <>
                <FaAngleDoubleDown size={14} />
                <span>Kaydır</span>
              </>
            )}
          </button>
          
          <div className="h-6 w-px bg-border-main mx-0.5" />
          
          <div className="p-1 hover:bg-surface-strong rounded-lg transition-colors">
            <SettingsModal
              type="sizden-gelen-dualar"
              autoScrollDuration={duration}
              setAutoScrollDuration={setDuration}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
